import { normalizeAssistantContent } from "../normalizeAssistantContent";
import { api, createDevAgentApiInstance, devAgentAuthHeaders, unwrap, withIdentity, withUserId } from "./core";
import type {
  DevAgentSession,
  DevAgentSessionState,
  MCPTool,
  UploadResult,
} from "./types";

export const createDevAgentSession = async (
  userId: string,
  orgId?: string,
): Promise<DevAgentSession> =>
  unwrap(
    (
      await api.post(
        withIdentity("/api/chat/sessions", userId, orgId),
        {
          user_id: userId,
          org_id: orgId,
          title: "OrgCompass",
          metadata: { mode: "dev_agent" },
        },
        { headers: devAgentAuthHeaders(orgId, userId) },
      )
    ).data,
  );

export const sendDevAgentMessage = async (
  uuid: string,
  params: {
    user_query: string;
    org_id?: string;
    file_input?: Array<{
      name: string;
      content?: string;
      metadataType?: string;
      id?: string;
    }>;
  },
): Promise<any> =>
  unwrap(
    (
      await api.post(withUserId(`/api/chat/sessions/${uuid}/messages`), params, {
        headers: devAgentAuthHeaders(params.org_id),
      })
    ).data,
  );

export const listMCPTools = async (userId?: string): Promise<MCPTool[]> =>
  unwrap(
    (
      await api.get(withUserId("/api/chat/mcp/tools", userId), {
        headers: devAgentAuthHeaders(undefined, userId),
      })
    ).data,
  );

export const listDevAgentSessions = async (
  userId: string,
  orgId?: string,
): Promise<DevAgentSession[]> => {
  const params = new URLSearchParams({ user_id: userId, status: "active" });
  if (orgId) params.append("org_id", orgId);
  const raw = unwrap<any>(
    (
      await api.get(`/api/chat/sessions?${params}`, {
        headers: devAgentAuthHeaders(orgId, userId),
      })
    ).data,
  );
  return raw?.sessions || (Array.isArray(raw) ? raw : []);
};

export const refreshSseTicket = async (sessionUuid: string): Promise<string> => {
  try {
    const state = unwrap<any>(
      (
        await api.get(withUserId(`/api/chat/sessions/${sessionUuid}/state`), {
          headers: devAgentAuthHeaders(),
        })
      ).data,
    );
    return state?.sse_ticket || "";
  } catch {
    return "";
  }
};

const sessionHistoryInflight = new Map<string, Promise<any[]>>();

export function getDevAgentSessionHistory(uuid: string): Promise<any[]> {
  const existing = sessionHistoryInflight.get(uuid);
  if (existing) return existing;

  const historyPromise = (async () => {
    const messages =
      unwrap<any>(
        (
          await api.get(withUserId(`/api/chat/sessions/${uuid}/history`), {
            headers: devAgentAuthHeaders(),
          })
        ).data,
      )?.messages || [];

    return messages.map((message: any) => ({
      ...message,
      content:
        typeof message?.content === "string"
          ? normalizeAssistantContent(message.content)
          : message?.content,
    }));
  })();

  sessionHistoryInflight.set(uuid, historyPromise);
  return historyPromise.finally(() => {
    if (sessionHistoryInflight.get(uuid) === historyPromise) {
      sessionHistoryInflight.delete(uuid);
    }
  });
}

export const approveDevAgentAction = async (
  uuid: string,
  params: { approved: boolean; approval_id?: string; response?: string },
): Promise<any> =>
  unwrap(
    (
      await api.post(withUserId(`/api/chat/sessions/${uuid}/approve`), params, {
        headers: devAgentAuthHeaders(),
      })
    ).data,
  );

export const renameDevAgentSession = async (
  uuid: string,
  title: string,
): Promise<DevAgentSession> =>
  unwrap(
    (
      await api.put(withUserId(`/api/chat/sessions/${uuid}`), { title }, {
        headers: devAgentAuthHeaders(),
      })
    ).data,
  );

export const deleteDevAgentSession = async (uuid: string): Promise<void> => {
  await api.delete(withUserId(`/api/chat/sessions/${uuid}`), {
    headers: devAgentAuthHeaders(),
  });
};

export const stopDevAgentExecution = async (uuid: string): Promise<void> => {
  await api.post(withUserId(`/api/chat/sessions/${uuid}/stop`), undefined, {
    headers: devAgentAuthHeaders(),
  });
};

export const uploadFile = async (file: File): Promise<UploadResult> => {
  const form = new FormData();
  form.append("file", file);
  const uploadApi = createDevAgentApiInstance();
  return unwrap(
    (
      await uploadApi.post(withUserId("/api/chat/upload"), form, {
        headers: { "Content-Type": undefined, ...devAgentAuthHeaders() },
      })
    ).data,
  );
};

export const listArtifacts = async (sessionUuid: string): Promise<any[]> =>
  unwrap<any>(
    (
      await api.get(withUserId(`/api/chat/sessions/${sessionUuid}/artifacts`), {
        headers: devAgentAuthHeaders(),
      })
    ).data,
  )?.artifacts || [];

export const listBackendArtifacts = async (sessionUuid: string): Promise<any[]> =>
  unwrap<any>(
    (
      await api.get(withUserId(`/api/artifacts/session/${sessionUuid}/list`), {
        headers: devAgentAuthHeaders(),
      })
    ).data,
  )?.artifacts || [];

export const getBackendArtifact = async (artifactId: string): Promise<any> =>
  unwrap<any>(
    (
      await api.get(withUserId(`/api/artifacts/${encodeURIComponent(artifactId)}`), {
        headers: devAgentAuthHeaders(),
      })
    ).data,
  );

export const importBackendArtifact = async (
  sessionUuid: string,
  artifact: {
    name: string;
    content: string;
    content_type?: string;
    metadata_type?: string;
    source_surface?: string;
    source_run_id?: string;
    source_artifact_name?: string;
    operation?: "created" | "modified" | "read" | "analyzed";
    previous_content?: string;
    summary?: string;
  },
): Promise<{ artifact: any; marker: string }> =>
  unwrap<any>(
    (
      await api.post(
        withUserId(`/api/artifacts/session/${sessionUuid}/import`),
        artifact,
        { headers: devAgentAuthHeaders() },
      )
    ).data,
  );

export const getSessionState = async (
  sessionUuid: string,
): Promise<DevAgentSessionState> =>
  unwrap(
    (
      await api.get(withUserId(`/api/chat/sessions/${sessionUuid}/state`), {
        headers: devAgentAuthHeaders(),
      })
    ).data,
  );

export const updateSessionState = async (
  sessionUuid: string,
  state: {
    impact_analysis?: any;
    test_coverage?: any;
    test_runs?: any[];
    session_summary?: any;
    session_cost?: any;
    dismissed_panels?: string[];
    dependencies?: import("./types").DependencySnapshot | null;
  },
): Promise<void> => {
  await api.put(withUserId(`/api/chat/sessions/${sessionUuid}/state`), state, {
    headers: devAgentAuthHeaders(),
  });
};

export const addArtifact = async (
  sessionUuid: string,
  artifact: { name: string; metadataType: string; metadataId: string },
): Promise<void> => {
  await api.post(withUserId(`/api/chat/sessions/${sessionUuid}/artifacts`), artifact, {
    headers: devAgentAuthHeaders(),
  });
};

export const removeArtifact = async (
  sessionUuid: string,
  artifactId: string,
): Promise<void> => {
  await api.delete(
    withUserId(`/api/chat/sessions/${sessionUuid}/artifacts/${artifactId}`),
    { headers: devAgentAuthHeaders() },
  );
};
